import { GDriveSheetData, GDriveSpreadsheetT } from 'infrastructure/googleDrive'
import { byStringFieldSort } from '../../Helpers'
import { INode } from 'shared/types/tree'
import { nodesOfTypeWithContent } from '../../utility/TreeHelpers'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import { UpdateMethod, UpdateType } from '../nodeUpdates/utility'
import { NodeBasedUpdates } from '../nodeUpdates/types'
import { allSkillIds } from '../nodeUpdates/skillsListNoteNode'

export function convertSheetDataToMappedSkills(sheetData: GDriveSpreadsheetT) {
  return mapSeriesStandards(sheetData.sheets[0].data[0])
}

function mapSeriesStandards({ rowData }: GDriveSheetData) {
  const userData = rowData.reduce<(string | undefined)[][]>((acc, data) => {
    return [...acc, data.values.map((value) => value?.formattedValue)]
  }, [])
  const headerIndex = userData.findIndex((row) =>
    row.find((v) => v?.toLowerCase().includes('series check')),
  )

  const mappedData = userData
    .slice(headerIndex + 1)
    .reduce<Record<string, string[]>>((seriesAcc, row) => {
      const seriesNum = row[0]
      if (!seriesNum) return seriesAcc
      const skillNames = row
        .slice(1)
        .reduce<Set<string>>((skillIdAcc, value) => {
          if (!value) return skillIdAcc
          const ids = value
            .split('\n')
            .filter((v) => v.trim())
            .map((v) => v.split(':')[0])
          if (ids[0]?.toLowerCase().includes('include')) {
            // We have a string like "Include all standards from Series 22 - Series 27"
            getSeriesRange(ids[0]).forEach((sNum) => {
              seriesAcc[String(sNum)].forEach((id) => skillIdAcc.add(id))
            })
            return skillIdAcc
          }
          ids.forEach((id) => skillIdAcc.add(id))
          return skillIdAcc
        }, new Set())
      return {
        ...seriesAcc,
        [seriesNum]: Array.from(skillNames),
      }
    }, {})
  return Object.entries(mappedData)
    .map(([seriesId, skillNames]) => ({
      seriesId,
      skillNames,
    }))
    .sort(byStringFieldSort('seriesId'))
}

function getSeriesRange(includeString: string) {
  // pull out of a string like this "Include all standards from Series 22 - Series 27"
  const [start, end] = includeString
    .toLowerCase()
    .split('-')
    .map((part) => {
      return parseInt(part.split('series').pop()!.trim())
    })
  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

export function getQuestionNodeForSeries(seriesNum: string, tree: INode) {
  const seriesTitle = `Series ${seriesNum.trim()}: Check`
  const [activityNode] = nodesOfTypeWithContent({
    tree,
    types: [NodeType.Activity],
    extraCheck: ({ title }) => title === seriesTitle,
  })
  if (!activityNode) return
  const [question] = nodesOfTypeWithContent({
    tree: activityNode,
    types: [NodeType.Question],
  })
  return question
}

export function getUpdatesForQuestionNode(
  skillIds: number[],
  questionNode: INode,
): NodeBasedUpdates[] {
  const [skillsList] = nodesOfTypeWithContent({
    tree: questionNode,
    types: [NodeType.SkillsList],
  })
  if (!skillsList) {
    return [
      {
        node: questionNode,
        extraInfo: 'Create Skills List',
        updates: [
          {
            type: UpdateType.node,
            ids: {
              nodeId: questionNode.id,
              treeId: questionNode.tree_id,
              versionId: questionNode.version,
            },
            params: {
              type: NodeType.SkillsList,
            },
            method: UpdateMethod.create,
          },
        ],
      },
    ]
  }
  const currentIds = allSkillIds(skillsList)
  const missing = skillIds.filter((id) => id && !currentIds.includes(id))
  if (!missing.length) return []
  return [
    {
      node: skillsList,
      extraInfo: `Add ${missing.length} Skill Leaves`,
      updates: [
        {
          type: UpdateType.leaf,
          ids: {
            nodeId: skillsList.id,
            treeId: skillsList.tree_id,
            versionId: skillsList.version,
          },
          params: {
            leaves: missing.map((mid) => ({
              type: LeafTypes.Skill,
              body: { skillID: Number(mid) },
            })),
          },
          method: UpdateMethod.create,
        },
      ],
    },
  ]
}
